import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  Spin,
  Space,
  Radio,
  Divider,
  Select,
} from "antd";
import { LoadingOutlined, UserOutlined } from "@ant-design/icons";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";
import {
  _cancel_delayed_delivery,
  _complete_intervention_order,
  _reassign_delivery_biker,
  _accept_intervention_order,
  _reject_intervention_order,
  _ready_intervention_order,
  _assign_kd_order,
  _reassign_kd_order,
  _get_intervention_available_bikers,
} from "../../../common/axios_services";

const { Option } = Select;

const InterventionActionsModal = ({
  visible,
  order,
  onCancel,
  onSuccess,
  violationType,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingBikers, setFetchingBikers] = useState(false);
  const [availableBikers, setAvailableBikers] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  React.useEffect(() => {
    if (visible && order?.id && selectedAction === "reassign") {
      fetchBikers();
    }
  }, [visible, order?.id, selectedAction]);

  const fetchBikers = async () => {
    try {
      setFetchingBikers(true);
      const response = await _get_intervention_available_bikers(order.id);
      setAvailableBikers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching bikers:", error);
      showErrorNotification("Failed to fetch available bikers");
    } finally {
      setFetchingBikers(false);
    }
  };

  if (!order) return null;

  const hasDelivery = Boolean(order.delivery?.id);
  const deliveryId = order.delivery?.id || order.id;

  const handleCancel = () => {
    form.resetFields();
    setSelectedAction(null);
    onCancel();
  };

  const handleAction = async (action, values) => {
    try {
      setLoading(true);
      let response;
      let successMessage = "";

      switch (action) {
        case "cancel":
          response = await _cancel_delayed_delivery(order.id, {
            reason: values.reason,
            refundAmount: values.refundAmount,
          });
          successMessage = "Delivery cancelled and buyer refunded";
          break;
        case "complete":
          response = await _complete_intervention_order(order.id, {
            reason: values.reason,
          });
          successMessage = "Order completed manually";
          break;
        case "reassign":
          response = await _reassign_delivery_biker(deliveryId, {
            newBikerId: values.newBikerId,
            reason: values.reason,
          });
          successMessage = "Biker reassigned successfully";
          break;
        case "accept":
          response = await _accept_intervention_order(order.id, {
            reason: values.reason,
            triggerDriver: false,
          });
          successMessage = "Order accepted";
          break;
        case "accept-delivery":
          response = await _accept_intervention_order(order.id, {
            reason: values.reason,
            triggerDriver: true,
          });
          successMessage = "Order accepted and driver triggered";
          break;
        case "assign-kd":
          response = await _assign_kd_order(order.id, {
            reason: values.reason,
          });
          successMessage = "Konnect driver assignment triggered";
          break;
        case "reassign-kd":
          response = await _reassign_kd_order(order.id, {
            reason: values.reason,
          });
          successMessage = "Konnect driver reassignment triggered";
          break;
        case "reject":
          response = await _reject_intervention_order(order.id, {
            reason: values.reason,
          });
          successMessage = "Order rejected and buyer refunded";
          break;
        case "ready":
          response = await _ready_intervention_order(order.id, {
            reason: values.reason,
          });
          successMessage = "Order marked as ready";
          break;
        default:
          throw new Error("Invalid action");
      }

      showSuccessNotification(successMessage);
      handleCancel();
      onSuccess();
    } catch (error) {
      showErrorNotification(
        error.response?.data?.message || `Failed to perform ${action}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const isNotAccept = violationType === "not-accept" || violationType === "NOT_ACCEPT";
  const isNotReady = violationType === "not-ready" || violationType === "NOT_READY";
  const isScheduled = violationType === "scheduled" || violationType === "SCHEDULED";

  const isWaitingToPickUp = violationType === "WAITING_TO_PICK_UP" || violationType === "waiting-to-pick-up";
  const isWaitingToStart = violationType === "WAITING_TO_START" || violationType === "waiting-to-start";
  const isWaitingToArrive = violationType === "WAITING_TO_ARRIVE" || violationType === "waiting-to-arrive";

  const showAcceptAction = false;
  const showAcceptDeliveryAction = false;

  const showAssignKDAction = violationType === "WAITING_TO_ACCEPT" || isWaitingToPickUp;
  const showReassignKDAction = violationType === "WAITING_TO_ACCEPT" || isWaitingToPickUp;

  const showRejectAction =
    isNotAccept ||
    isNotReady ||
    violationType === "not-picked-buyer" ||
    violationType === "not-picked-driver" ||
    violationType === "NOT_PICKED" ||
    violationType === "not-delivered-buyer" ||
    violationType === "not-delivered-driver" ||
    violationType === "NOT_DELIVERED" ||
    violationType === "WAITING_TO_ACCEPT" ||
    isWaitingToPickUp ||
    isWaitingToStart ||
    isWaitingToArrive ||
    isScheduled;

  const showReadyAction = false;

  const showCompleteAction =
    isWaitingToStart ||
    isWaitingToArrive ||
    violationType === "not-picked-buyer" ||
    violationType === "not-delivered-buyer" ||
    violationType === "not-delivered-driver" ||
    violationType === "NOT_DELIVERED";

  const showReassignAction =
    (violationType === "WAITING_TO_ACCEPT" ||
      isWaitingToPickUp ||
      violationType === "not-picked-driver" ||
      violationType === "NOT_PICKED" ||
      isScheduled) &&
    hasDelivery;

  return (
    <Modal
      title={`Intervention Actions - Order #${order.orderCode}`}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
    >
      <Spin spinning={loading} indicator={antIcon}>
        <div className="space-y-6">
          <Radio.Group
            value={selectedAction}
            onChange={(e) => {
              setSelectedAction(e.target.value);
              form.resetFields();
            }}
            className="flex flex-col gap-4"
          >
            {showAssignKDAction && (
              <div className="border rounded-lg p-4 hover:bg-gray-50 border-blue-200 bg-blue-50/30">
                <Radio value="assign-kd" className="text-lg">
                  <span className="font-semibold text-blue-700">Assign Konnect Driver</span>
                </Radio>
                <p className="text-gray-600 ml-6 mt-2">
                  Find and assign an available Konnect driver for this delivery
                </p>
              </div>
            )}

            {showReassignKDAction && (
              <div className="border rounded-lg p-4 hover:bg-gray-50 border-orange-200 bg-orange-50/30">
                <Radio value="reassign-kd" className="text-lg">
                  <span className="font-semibold text-orange-700">Reassign Konnect Driver</span>
                </Radio>
                <p className="text-gray-600 ml-6 mt-2">
                  Replace current driver and find a new Konnect driver
                </p>
              </div>
            )}

            {showRejectAction && (
              <div className="border rounded-lg p-4 hover:bg-gray-50 border-red-200 bg-red-50/30">
                <Radio value="reject" className="text-lg">
                  <span className="font-semibold text-red-700">Reject Order</span>
                </Radio>
                <p className="text-gray-600 ml-6 mt-2">
                  Reject the order and refund the buyer
                </p>
              </div>
            )}

            {showCompleteAction && (
              <div className="border rounded-lg p-4 hover:bg-gray-50 border-purple-200 bg-purple-50/30">
                <Radio value="complete" className="text-lg">
                  <span className="font-semibold text-purple-700">Investigate / Resolve Order</span>
                </Radio>
                <p className="text-gray-600 ml-6 mt-2">
                  Investigate delivery status and mark order as{" "}
                  {order.deliveryType === "PICKUP" ? "PICKED" : "DELIVERED"}
                  {order.deliveryType === "DELIVERY" && " and release payment"}
                </p>
              </div>
            )}

            {showReassignAction && (
              <div className="border rounded-lg p-4 hover:bg-gray-50">
                <Radio value="reassign" className="text-lg">
                  <span className="font-semibold">Reassign Biker (Manual ID)</span>
                </Radio>
                <p className="text-gray-600 ml-6 mt-2">
                  Remove current biker and assign a different available biker manually
                </p>
              </div>
            )}
          </Radio.Group>

          <Divider />

          {selectedAction && (
            <Form
              form={form}
              layout="vertical"
              onFinish={(values) => handleAction(selectedAction, values)}
            >
              {selectedAction === "cancel" && (
                <>
                  <Form.Item
                    label="Reason for Cancellation"
                    name="reason"
                    rules={[
                      {
                        required: true,
                        message: "Please provide a reason",
                      },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="e.g., Driver unavailable, Order too late"
                      rows={3}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Refund Amount (Optional)"
                    name="refundAmount"
                    extra="Leave empty to refund full order amount"
                  >
                    <InputNumber
                      min={0}
                      prefix="₦"
                      placeholder="Custom refund amount"
                      className="w-full"
                    />
                  </Form.Item>
                </>
              )}

              {selectedAction === "complete" && (
                <Form.Item
                  label="Reason for Investigation / Resolution"
                  name="reason"
                  rules={[
                    {
                      required: true,
                      message: "Please provide a reason",
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder="e.g., Driver confirmed pickup/delivery, order completed"
                    rows={3}
                  />
                </Form.Item>
              )}

              {["accept", "accept-delivery", "reject", "ready", "assign-kd", "reassign-kd"].includes(selectedAction) && (
                <Form.Item
                  label={`Reason for ${selectedAction.replace("-", " ")}`}
                  name="reason"
                  rules={[
                    {
                      required: true,
                      message: "Please provide a reason",
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder={`e.g., Manual intervention for ${selectedAction}`}
                    rows={3}
                  />
                </Form.Item>
              )}

              {selectedAction === "reassign" && (
                <>
                  <Form.Item
                    label="Select New Biker"
                    name="newBikerId"
                    rules={[
                      {
                        required: true,
                        message: "Please select a biker",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select an available biker"
                      loading={fetchingBikers}
                      showSearch
                      optionFilterProp="children"
                    >
                      {availableBikers.map((biker) => (
                        <Option key={biker.id} value={biker.id}>
                          <div className="flex justify-between items-center">
                            <span>
                              <UserOutlined className="mr-2" />
                              {biker.name}
                            </span>
                            <span className="text-xs text-gray-400">
                              {biker.bikerType} • {biker.deliveryMode}
                            </span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Reason for Reassignment"
                    name="reason"
                    rules={[
                      {
                        required: true,
                        message: "Please provide a reason",
                      },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="e.g., Original biker refusing delivery, Vehicle breakdown"
                      rows={3}
                    />
                  </Form.Item>
                </>
              )}

              <Form.Item>
                <Space className="w-full justify-between">
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    danger={selectedAction === "reject" || selectedAction === "cancel"}
                  >
                    {selectedAction === "accept" && "Accept Order"}
                    {selectedAction === "accept-delivery" && "Accept & Trigger Driver"}
                    {selectedAction === "assign-kd" && "Assign Konnect Driver"}
                    {selectedAction === "reassign-kd" && "Reassign Konnect Driver"}
                    {selectedAction === "reject" && "Reject Order"}
                    {selectedAction === "ready" && "Confirm Ready"}
                    {selectedAction === "cancel" && "Cancel & Refund"}
                    {selectedAction === "complete" && "Investigate & Resolve"}
                    {selectedAction === "reassign" && "Reassign Biker"}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default InterventionActionsModal;
